import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { useAuth } from '../lib/auth'
import { supabase } from '../lib/supabase'

// aircargomcp.com has no separate dashboard: the whole family shares ONE Navo24 workspace (the hub at
// trackingmcp.com/home) where Air is already a service card alongside Tracking, Schedules, Loading and
// Rates. "Dashboard" here hands the signed-in user straight into that hub via a single-use SSO nonce
// (mint_sso_handoff on the shared Supabase → redeemed by the host's adopt-session), so they arrive
// authenticated with no second sign-in. One workspace, air as an add-on, same as every product.
const WORKSPACE_URL =
  (import.meta.env.VITE_WORKSPACE_URL as string) ?? 'https://trackingmcp.com/home'

export function DashboardPage() {
  const { session, loading } = useAuth()
  const nav = useNavigate()
  const [failed, setFailed] = useState(false)
  const handedOff = useRef(false)

  useEffect(() => {
    if (loading) return
    if (!session) {
      nav('/login', { replace: true })
      return
    }
    if (handedOff.current) return
    handedOff.current = true
    ;(async () => {
      try {
        // Mint a 60s single-use nonce keyed to THIS session; only the nonce travels in the URL.
        const { data: nonce, error } = await supabase.rpc('mint_sso_handoff', {
          p_access_token: session.access_token,
          p_refresh_token: session.refresh_token,
        })
        if (error || !nonce) throw error ?? new Error('no nonce')
        window.location.replace(`${WORKSPACE_URL}#sso=${encodeURIComponent(nonce as string)}`)
      } catch {
        // Hand-off failed (e.g. RPC blip): still send them to the hub; they sign in there once.
        setFailed(true)
        window.location.replace(WORKSPACE_URL)
      }
    })()
  }, [loading, session, nav])

  return (
    <Layout>
      <main className="mx-auto max-w-md px-6 pt-24 pb-16 text-center">
        <div className="mono text-[11px] uppercase tracking-wider text-faint">Navo24 workspace</div>
        <h1
          className="mt-3 font-display text-2xl text-ink"
          style={{ fontWeight: 220, letterSpacing: '-0.02em' }}
        >
          Opening your workspace…
        </h1>
        <p className="mt-3 text-sm text-muted">
          Taking you to your Navo24 account, where Air cargo sits alongside your other services.
        </p>
        {failed && (
          <p className="mt-4 text-sm text-muted">
            Taking a moment.{' '}
            <a className="font-semibold text-signal hover:underline" href={WORKSPACE_URL}>
              Continue to the workspace
            </a>
            .
          </p>
        )}
      </main>
    </Layout>
  )
}
